import { store, store$ } from '@redux/store';
import { map, filter, distinctUntilChanged, switchMap, catchError, of, shareReplay } from 'rxjs';
import LoadingStatus from 'enums/loadingStatus';
import getArticleSearchResults$ from '../apis/getArticleSearchResults';
import blogSearchResultsSlice from '../redux/slices';
import { selectSearchResultsModifiers,
    selectArticlesSearchCommand, selectFetchingArticlesStatus } from '../redux/selectors';

const { actions } = blogSearchResultsSlice;

/**
 * Observable that listens for changes in the Redux store and emits when the article search results are being fetched.
 *
 * This observable:
 * - Maps the Redux state to the `SearchResultsModifiers`.
 * - Filters for states where the `fetchingArticlesStatus` is set to `Loading`.
 * - Ensures distinct emissions using `distinctUntilChanged`.
 *
 * @type {Observable<Object>}
 */
export const onloadingArticleSearchResults$ = store$.pipe(
    map(selectSearchResultsModifiers),
    distinctUntilChanged(),
    filter(({ fetchingArticlesStatus }) =>
        fetchingArticlesStatus.name === LoadingStatus.Loading.toString()
    ),
);

/**
 * Observable pipeline that listens for changes in the fetching articles status
 * and emits only when the status indicates a successful fetch.
 *
 * @constant
 * @type {Observable}
 * @returns {Observable} Emits when the fetchingArticlesStatus.name equals "Succeeded".
 */
export const onSuccesedArticleSearchResults$ = store$.pipe(
  map(selectFetchingArticlesStatus),
  distinctUntilChanged(),
  filter((fetchingArticlesStatus) =>
    fetchingArticlesStatus.name === LoadingStatus.Succeeded.toString()),
);

/**
 * Observable that fetches the article search results when triggered by `onloadingArticleSearchResults$`.
 *
 * This observable:
 * - Switches to a new observable that fetches the article search results from the API.
 * - Dispatches success or error actions to the Redux store based on the API response.
 * - Caches the latest result using `shareReplay(1)` for reuse.
 *
 * @type {Observable<string>}
 */
export const onArticleSearchResultsLoaded$ = onloadingArticleSearchResults$.pipe(
    switchMap(() => {
        const state = store.getState();
        const searchParameters = selectArticlesSearchCommand(state);
        return getArticleSearchResults$(searchParameters).pipe(
            switchMap((response) => {
                store.dispatch(actions.fetchArticlesSuccess());
                return of(response);
            }),
            catchError((error) => {
                store.dispatch(actions.fetchArticlesError(error));
                return of('');
            })
        );
    }),
    shareReplay(1)
);